import axios from 'axios'
import { getResponseData } from 'src/queries/network.utils'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'

import {
  CompleteVendorAccountCollectionFlowInput,
  ConfirmVendorAccountCollectionFlowInput,
  CreateVendorMutationInput,
  CreateVendorRequest,
  isVendorArchived,
  ListVendorsParams,
  UpdateVendorDetailsMutationInput,
  Vendor,
  VendorAccountCollectionFlowCompletionResponse,
  VendorAccountCollectionFlowConfirmationResponse,
  VendorAccountCollectionFlowResponse,
  VendorPaymentMethodDetailsResponse,
  VendorResponse,
  VendorsList,
  VendorsPageResponse,
  VendorStatus,
} from './vendors.types'

const VENDORS_BASE = '/payments/vendors'

type CreateVendorWireRequest = {
  name: string
  email: string
  externalId?: string
  contactName?: string
  phoneNumber?: string
  description?: string
}

/** Nullish-safe on purpose: the query factories call these fns through `!`
 * assertions, so a consumer that overrides `enabled` reaches here with
 * `undefined`. Guarding on the value itself (rather than `value.trim()`) turns
 * that into the named "<field> is required" error instead of an opaque
 * `Cannot read properties of undefined (reading 'trim')` TypeError. */
const requireNonBlank = (
  value: string | null | undefined,
  field: string,
): void => {
  if (!value?.trim()) {
    throw new Error(`${field} is required`)
  }
}

const vendorPath = (vendorId: string): string => {
  requireNonBlank(vendorId, 'vendorId')
  return `${VENDORS_BASE}/${encodeURIComponent(vendorId)}`
}

const toCreateVendorWireRequest = (
  request: CreateVendorRequest,
): CreateVendorWireRequest => {
  const wireRequest: CreateVendorWireRequest = {
    name: request.name,
    email: request.email,
  }

  if (request.externalId !== undefined) {
    wireRequest.externalId = request.externalId
  }
  if (request.contactName !== undefined) {
    wireRequest.contactName = request.contactName
  }
  if (request.phone !== undefined) {
    wireRequest.phoneNumber = request.phone
  }
  if (request.notes !== undefined) {
    wireRequest.description = request.notes
  }

  return wireRequest
}

export function mapVendorResponseToVendor(response: VendorResponse): Vendor {
  return {
    id: response.id,
    name: response.name,
    email: response.email,
    externalId: response.externalId ?? undefined,
    contactName: response.contactName ?? undefined,
    phone: response.phoneNumber ?? undefined,
    notes: response.description ?? undefined,
    status: isVendorArchived(response)
      ? VendorStatus.Archived
      : response.paymentMethod?.status === 'PAYABLE'
        ? VendorStatus.Active
        : VendorStatus.ActionRequired,
    paymentMethod: response.paymentMethod ?? null,
    ...(response.accountCollectionFlow
      ? { accountCollectionFlow: response.accountCollectionFlow }
      : {}),
    ...(response.archivedAt != null ? { archivedAt: response.archivedAt } : {}),
    createdAt: response.createdAt,
    updatedAt: response.updatedAt,
  }
}

export async function getVendors(
  params: ListVendorsParams,
): Promise<VendorsList> {
  const searchParams = new URLSearchParams({
    page: String(params.page),
    count: String(params.count),
  })
  if (params.search) {
    searchParams.set('search', params.search)
  }

  const response = await getResponseData(
    axios.get<VendorsPageResponse>(
      `${VENDORS_BASE}?${searchParams.toString()}`,
      getRequestConfig(RequestConfig.Default),
    ),
  )

  return {
    vendors: response.vendors.map(mapVendorResponseToVendor),
    pagination: response.pagination,
  }
}

/**
 * Realtime read of one vendor payment method's display-safe bank details —
 * `GET /payments/vendors/{vendorId}/payment-methods/{paymentMethodId}`. The
 * wire DTO is already a plain, display-safe shape (no raw account numbers), so
 * it is returned verbatim rather than mapped. Both path segments are guarded so
 * a missing id never builds a malformed URL and fires it.
 */
export async function getVendorPaymentMethodDetails(
  vendorId: string,
  paymentMethodId: string,
): Promise<VendorPaymentMethodDetailsResponse> {
  requireNonBlank(paymentMethodId, 'paymentMethodId')
  return getResponseData(
    axios.get<VendorPaymentMethodDetailsResponse>(
      `${vendorPath(vendorId)}/payment-methods/${encodeURIComponent(paymentMethodId)}`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function getVendor(vendorId: string): Promise<Vendor> {
  const response = await getResponseData(
    axios.get<VendorResponse>(
      vendorPath(vendorId),
      getRequestConfig(RequestConfig.Default),
    ),
  )
  return mapVendorResponseToVendor(response)
}

export async function createVendor(
  input: CreateVendorMutationInput,
): Promise<Vendor> {
  requireNonBlank(input.idempotencyKey, 'idempotencyKey')
  const config = getRequestConfig(RequestConfig.Default)
  const response = await getResponseData(
    axios.post<VendorResponse>(
      VENDORS_BASE,
      toCreateVendorWireRequest(input.request),
      {
        ...config,
        headers: {
          ...config.headers,
          'Idempotency-Key': input.idempotencyKey,
        },
      },
    ),
  )
  return mapVendorResponseToVendor(response)
}

/**
 * Edit a vendor's Step-1 details — `PATCH /payments/vendors/{vendorId}` with a
 * `{ details: {...} }` body (EX-40771). The controller accepts exactly one of
 * `{ archived }` or `{ details }`; this is the details operation. `details` is a
 * full replace, so the caller sends all six domain fields and the shared wire
 * mapper omits blank optionals (which the server reads as "clear").
 *
 * `acknowledged` + `acknowledgementCopyVersion` go INSIDE `details` (mandatory
 * re-acknowledgement, PO 2026-08-18). That is where extend-api reads them as of
 * #10138 (merged 2026-08-19): they are fields on `PatchVendorDetailsRequest`,
 * and the controller returns 422 `VENDOR_ACKNOWLEDGEMENT_REQUIRED` when
 * `details.acknowledged != true`. Sent as top-level siblings they are simply
 * ignored, so every save would fail acknowledgement validation.
 *
 * The response is a full re-read `VendorResponse`, mapped like every other
 * vendor read so the caller gets a plain domain `Vendor`.
 */
export async function updateVendorDetails(
  input: UpdateVendorDetailsMutationInput,
): Promise<Vendor> {
  requireNonBlank(
    input.acknowledgementCopyVersion,
    'acknowledgementCopyVersion',
  )
  const response = await getResponseData(
    axios.patch<VendorResponse>(
      vendorPath(input.vendorId),
      {
        details: {
          ...toCreateVendorWireRequest(input.request),
          acknowledged: true,
          acknowledgementCopyVersion: input.acknowledgementCopyVersion,
        },
      },
      getRequestConfig(RequestConfig.Default),
    ),
  )
  return mapVendorResponseToVendor(response)
}

export async function createVendorAccountCollectionFlow(
  vendorId: string,
): Promise<VendorAccountCollectionFlowResponse> {
  return getResponseData(
    axios.post<VendorAccountCollectionFlowResponse>(
      `${vendorPath(vendorId)}/account-collection-flow`,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function confirmVendorAccountCollectionFlow(
  input: ConfirmVendorAccountCollectionFlowInput,
): Promise<VendorAccountCollectionFlowConfirmationResponse> {
  requireNonBlank(input.flowId, 'flowId')
  requireNonBlank(
    input.acknowledgementCopyVersion,
    'acknowledgementCopyVersion',
  )
  if (input.acknowledged !== true) {
    throw new Error('acknowledged must be true')
  }

  return getResponseData(
    axios.post<VendorAccountCollectionFlowConfirmationResponse>(
      `${vendorPath(input.vendorId)}/account-collection-flow/${encodeURIComponent(input.flowId)}/confirm`,
      {
        acknowledged: true,
        acknowledgementCopyVersion: input.acknowledgementCopyVersion,
      },
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function completeVendorAccountCollectionFlow(
  input: CompleteVendorAccountCollectionFlowInput,
): Promise<VendorAccountCollectionFlowCompletionResponse> {
  requireNonBlank(input.flowId, 'flowId')

  return getResponseData(
    axios.post<VendorAccountCollectionFlowCompletionResponse>(
      `${vendorPath(input.vendorId)}/account-collection-flow/${encodeURIComponent(input.flowId)}/complete`,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}
